import { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, endOfMonth, format, startOfMonth } from 'date-fns';
import { slotApi } from '../../../services';
import { SlotBookingConfig, SlotItem } from './slotTypes';
import {
  generateCustomSlots,
  hasUnresolvedPlaceholder,
  normalizeApiSlots,
  resolveUrlTemplate,
} from './slotUtils';

interface UseSlotsResult {
  slots: SlotItem[];
  loading: boolean;
  error: string;
  /** The API URL needs a `{{param}}` that is not on the page URL. */
  missingParams: boolean;
}

/* One extra day either side so slots that spill across midnight in the
   viewer's timezone still land on the right calendar day. */
const monthRange = (month: Date) => ({
  start: addDays(startOfMonth(month), -1),
  end: addDays(endOfMonth(month), 1),
});

export const useSlots = (
  config: SlotBookingConfig,
  visibleMonth: Date,
  searchParams: URLSearchParams,
): UseSlotsResult => {
  const [apiSlots, setApiSlots] = useState<SlotItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cacheRef = useRef<Map<string, SlotItem[]>>(new Map());

  const { source, api, custom } = config;
  const monthKey = format(visibleMonth, 'yyyy-MM');
  const resolvedUrl = useMemo(
    () => (api.url ? resolveUrlTemplate(api.url.trim(), searchParams) : ''),
    [api.url, searchParams],
  );
  const missingParams = source === 'api' && Boolean(api.url) && hasUnresolvedPlaceholder(api.url, searchParams);

  const customSlots = useMemo(() => {
    if (source !== 'custom') return [];
    const { start, end } = monthRange(visibleMonth);
    return generateCustomSlots(custom, start, end);
  }, [source, custom, monthKey]);

  useEffect(() => {
    if (source !== 'api') return;
    if (!resolvedUrl || missingParams) {
      setApiSlots([]);
      setError('');
      setLoading(false);
      return;
    }

    const { start, end } = monthRange(visibleMonth);
    const filter = api.sendRange
      ? { startAt: format(start, 'yyyy-MM-dd'), endAt: format(end, 'yyyy-MM-dd') }
      : undefined;
    const cacheKey = `${resolvedUrl}|${api.path}|${filter ? monthKey : 'all'}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setApiSlots(cached);
      setError('');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    slotApi
      .fetchSlots(resolvedUrl, filter)
      .then((data) => {
        const now = Date.now();
        const slots = normalizeApiSlots(data, api.path).filter(
          (slot) => new Date(slot.iso).getTime() > now,
        );
        cacheRef.current.set(cacheKey, slots);
        if (!cancelled) setApiSlots(slots);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Slot booking: failed to load slots', err);
        setApiSlots([]);
        setError('Could not load available slots. Please try again later.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [source, resolvedUrl, missingParams, api.path, api.sendRange, monthKey]);

  return {
    slots: source === 'api' ? apiSlots : customSlots,
    loading: source === 'api' && loading,
    error: source === 'api' ? error : '',
    missingParams,
  };
};
